/**
 * PasswordInput — a raw password <input> with an accessible show/hide eye
 * toggle. Used on the login and reset-password pages. It forwards standard
 * input props *and* a ref through to the inner <input>, so it works both with
 * plain useState and with React Hook Form's `register()` (which needs the ref
 * to read the field value — see frontend/CLAUDE.md). It owns only the local
 * visibility state.
 *
 * The toggle is type="button" so it never submits a surrounding <form>, and the
 * input carries pr-10 so the masked text never runs under the icon. The base
 * class matches the existing login/reset inputs to keep the visual unchanged.
 */
import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";

const BASE_INPUT_CLASS =
  "w-full border border-brand-steel/50 bg-white rounded-lg px-3 py-2.5 pr-10 text-sm " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-crimson/30";

export type PasswordInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type"
>;

export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ className, ...props }, ref) {
    const [visible, setVisible] = useState(false);

    return (
      <div className="relative">
        <input
          {...props}
          ref={ref}
          type={visible ? "text" : "password"}
          className={className ? `${BASE_INPUT_CLASS} ${className}` : BASE_INPUT_CLASS}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Hide password" : "Show password"}
          aria-pressed={visible}
          className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center h-6 w-6 rounded text-brand-steel hover:text-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-crimson/30"
        >
          {visible ? (
            <EyeOff size={16} aria-hidden="true" />
          ) : (
            <Eye size={16} aria-hidden="true" />
          )}
        </button>
      </div>
    );
  },
);

export default PasswordInput;
